import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import { Card, CardContent } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { Spinner } from 'components/ui/spinner';
import { toast } from 'react-toastify';
import { UniversalDetailPage } from 'shared/components/UniversalDetailPage';
import { ASSET_ENTITY_CONFIG } from 'shared/configs/entities/assetEntity.config';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { Asset, AssetFieldDefinition, AssetTypeFieldDefinition, FileRequirement, RequirementType } from 'shared/types/asset';
import { FileText, Image as ImageIcon, Upload, Package, CheckCircle, WarningCircle, Info, Plus } from '@phosphor-icons/react';
import type { UniversalEntityConfig, UniversalFieldConfig } from 'shared/types/universalEntityConfig';
import { InlineFieldCreator, NewFieldData } from 'shared/components/InlineFieldCreator';
import { useCreateTypeFieldDefinition, useSuggestedFiles } from 'hooks/useAssets';
import { AssetQRCodeSection } from 'components/assets/AssetQRCodeSection';
import { MaintenanceCalendar } from 'shared/components/maintenance/sections/MaintenanceCalendar';
import { QrCode, CalendarBlank, ChartLine, ListBullets } from '@phosphor-icons/react';
import { useAssetMaintenanceSchedules, useMaintenanceEvents } from 'hooks/useAssets';
import { MaintenanceTimeline } from 'components/assets/MaintenanceTimeline';
import { MaintenanceCreateDialog } from 'components/assets/MaintenanceCreateDialog';
import AssetChildRecordsSection from './sections/AssetChildRecordsSection';

/**
 * Convert AssetFieldDefinition or AssetTypeFieldDefinition to UniversalFieldConfig for UniversalDetailPage
 */
const convertFieldDefinitionToFieldConfig = (
  fieldDef: AssetFieldDefinition | AssetTypeFieldDefinition,
  assetTypeId?: number,
): UniversalFieldConfig => {
  // Map field types
  let fieldType: UniversalFieldConfig['type'] = 'text';
  let pickerMode: UniversalFieldConfig['pickerMode'] = undefined;
  let pickerConfig: any = undefined;

  if (fieldDef.fieldType === 'number') {
    fieldType = 'number';
  } else if (fieldDef.fieldType === 'date') {
    fieldType = 'date';
  } else if (fieldDef.fieldType === 'boolean') {
    // Boolean as picker with Ja/Nee options
    fieldType = 'picker';
    pickerMode = 'custom-tags';
    pickerConfig = {
      customOptions: [
        { label: 'Ja', value: 'true' },
        { label: 'Nee', value: 'false' },
      ],
    };
  } else if (fieldDef.fieldType === 'enum' && fieldDef.enumValues?.length) {
    // Enum as picker — filter by type if enumTypeMapping exists
    fieldType = 'picker';
    pickerMode = 'custom-tags';
    const mapping = (fieldDef as AssetFieldDefinition).enumTypeMapping;
    const filteredValues = fieldDef.enumValues.filter((val) => {
      if (!mapping || !mapping[val] || mapping[val].length === 0) return true; // no restriction
      return assetTypeId ? mapping[val].includes(assetTypeId) : true;
    });
    pickerConfig = {
      customOptions: filteredValues.map((val) => ({
        label: val,
        value: val,
      })),
    };
  } else if (fieldDef.fieldType === 'multi_enum' && fieldDef.enumValues?.length) {
    // Multi-enum as picker — filter by type if enumTypeMapping exists
    fieldType = 'picker';
    pickerMode = 'custom-tags';
    const mapping = (fieldDef as AssetFieldDefinition).enumTypeMapping;
    const filteredValues = fieldDef.enumValues.filter((val) => {
      if (!mapping || !mapping[val] || mapping[val].length === 0) return true;
      return assetTypeId ? mapping[val].includes(assetTypeId) : true;
    });
    pickerConfig = {
      customOptions: filteredValues.map((val) => ({
        label: val,
        value: val,
      })),
      multiSelect: true,
    };
  } else if (fieldDef.fieldType === 'company_reference') {
    // Company reference as supplier picker (links to CRM ContactCompany)
    fieldType = 'picker';
    pickerMode = 'supplier';
    pickerConfig = {
      searchPlaceholder: `Zoek ${fieldDef.displayName.toLowerCase()}...`,
      allowRemoval: true,
    };
  }

  const config: UniversalFieldConfig = {
    id: fieldDef.fieldName,
    label: fieldDef.displayName,
    type: fieldType,
    icon: undefined,
    pickerMode,

    tableConfig: {
      displayInTable: false,
    },

    detailConfig: {
      editable: true,
      required: fieldDef.isRequired,
      placeholder: fieldDef.metadata?.placeholder,
      section: 'main',
      ...(fieldDef.fieldType === 'company_reference' ? {
        customDisplayRenderer: (asset: Asset) => {
          const raw = asset.fieldValues?.[fieldDef.fieldName];
          if (!raw) return null;
          const company = typeof raw === 'object' ? raw : { id: null, name: raw };
          if (company.id) {
            return (
              <a href={`/company/${company.id}`} target="_blank" rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
                onClick={(e: any) => e.stopPropagation()}>
                {company.name}
              </a>
            );
          }
          return <span>{company.name}</span>;
        },
      } : {}),
    },

    getValue: (asset: Asset) => {
      const raw = asset.fieldValues?.[fieldDef.fieldName];
      if (fieldDef.fieldType === 'company_reference' && raw) {
        if (typeof raw === 'object' && raw.id) return raw;
        if (typeof raw === 'string') return { id: null, name: raw };
      }
      return raw;
    },
    formatDisplay: (value: any) => {
      if (value === null || value === undefined) return '-';
      if (fieldDef.fieldType === 'boolean') return value ? 'Ja' : 'Nee';
      if (fieldDef.fieldType === 'date' && value) {
        return new Date(value).toLocaleDateString('nl-NL');
      }
      if (fieldDef.fieldType === 'multi_enum' && Array.isArray(value)) {
        return value.join(', ');
      }
      if (fieldDef.fieldType === 'company_reference') {
        if (typeof value === 'object' && value?.name) return value.name;
        if (typeof value === 'string') return value;
        return '-';
      }
      return String(value);
    },
    buildUpdatePayload: (value: any) => {
      let processedValue = value;
      if (fieldDef.fieldType === 'boolean') {
        processedValue = value === 'true' || value === true;
      }
      if (fieldDef.fieldType === 'company_reference') {
        // Store as {id, name} object for CRM link
        processedValue = value ? { id: value.id, name: value.name } : null;
      }
      return {
        fieldValues: {
          [fieldDef.fieldName]: processedValue,
        },
      };
    },
  };

  // Add picker config if defined
  if (pickerConfig) {
    (config as any).pickerConfig = pickerConfig;
  }

  return config;
};

/**
 * Documents section component for asset detail page
 */
const AssetDocumentsSection: React.FC<{ entity: Asset; onUpdate: (updated: Asset) => void }> = ({
  entity: asset,
}) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [assetFileLogs, setAssetFileLogs] = useState<any[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [fileRequirements, setFileRequirements] = useState<FileRequirement[]>([]);
  const { suggestedFiles, refetch: refetchSuggested } = useSuggestedFiles(asset?.id);

  // Link a suggested file to this asset
  const handleLinkSuggestedFile = async (fileLogId: number) => {
    if (!session || !asset) return;
    try {
      await fetchWithAuth(
        `${API_URL}/assets/${asset.id}/files`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileLogId }),
        },
        session,
      );
      // Refresh both file lists
      refetchSuggested();
      // Re-fetch asset files
      const resp = await fetchWithAuth(`${API_URL}/assets/${asset.id}/files`, { method: 'GET' }, session);
      if (resp.ok) setAssetFileLogs(await resp.json());
    } catch (err) {
      console.error('Failed to link file:', err);
    }
  };

  // Fetch file logs and requirements
  useEffect(() => {
    if (!asset || !session) return;

    const fetchFileData = async () => {
      try {
        setIsLoadingFiles(true);

        // Fetch asset file logs using proper relationship
        const fileLogsResponse = await fetchWithAuth(
          `${API_URL}/assets/${asset.id}/files`,
          { method: 'GET' },
          session
        );

        if (fileLogsResponse.ok) {
          const data = await fileLogsResponse.json();
          console.log('📄 [Documents] Asset file logs loaded:', data);
          setAssetFileLogs(data);
        } else {
          console.error('❌ [Documents] Failed to fetch asset files:', fileLogsResponse.status);
        }

        // Fetch file requirements from asset type (not category)
        if (asset.assetTypeId) {
          const requirementsResponse = await fetchWithAuth(
            `${API_URL}/file-requirements?assetTypeId=${asset.assetTypeId}`,
            { method: 'GET' },
            session
          );

          if (requirementsResponse.ok) {
            const requirements = await requirementsResponse.json();
            const assetScope = asset.roomId ? 'room' : asset.unitId ? 'unit' : 'building';
            const activeRequirements = requirements.filter((req: FileRequirement) => {
              if (!req.active) return false;
              const scopes = (req as any).scopes;
              if (!scopes || scopes.length === 0) return true; // null/empty = all scopes
              return scopes.includes(assetScope);
            });
            setFileRequirements(activeRequirements);
          } else {
            console.error('❌ [Documents] Failed to fetch requirements:', requirementsResponse.status);
          }
        } else {
          console.warn('⚠️ [Documents] No assetTypeId found');
        }
      } catch (error) {
        console.error('❌ [Documents] Error fetching file data:', error);
      } finally {
        setIsLoadingFiles(false);
      }
    };

    fetchFileData();
  }, [asset, session]);

  // Filter documents (exclude pictures)
  const documentFileLogs = useMemo(() => {
    const filtered = assetFileLogs.filter((assetFileLog) => {
      const fileLog = assetFileLog.fileLog;
      // Check if any of the fileLabels has documentType 'picture'
      const isPicture = fileLog.fileLabels?.some((label: any) => label.documentType === 'picture');
      return !isPicture; // Only include non-pictures
    });
    console.log('📄 [Documents] Filtered document file logs:', filtered);
    return filtered;
  }, [assetFileLogs]);

  const documentRequirements = useMemo(() => {
    const filtered = fileRequirements.filter((req) =>
      !req.fileLabel || (req.fileLabel as any).documentType !== 'picture'
    );
    console.log('📋 [Documents] Filtered document requirements:', filtered);
    return filtered;
  }, [fileRequirements]);

  const getRequirementBadgeVariant = (requirementType: RequirementType | string) => {
    const type = typeof requirementType === 'string' ? requirementType.toUpperCase() : requirementType;

    switch (type) {
      case RequirementType.REQUIRED:
      case 'REQUIRED':
        return { variant: 'destructive' as const, icon: WarningCircle, label: 'Verplicht' };
      case RequirementType.RECOMMENDED:
      case 'RECOMMENDED':
        return { variant: 'outline' as const, icon: Info, label: 'Aanbevolen' };
      case RequirementType.OPTIONAL:
      case 'OPTIONAL':
        return { variant: 'secondary' as const, icon: CheckCircle, label: 'Optioneel' };
      default:
        return { variant: 'secondary' as const, icon: Info, label: 'Optioneel' };
    }
  };

  // Group files by their required label
  const filesByRequirement = useMemo(() => {
    const grouped = new Map<string, any[]>();
    const otherFiles: any[] = [];

    documentFileLogs.forEach((assetFileLog) => {
      const fileLog = assetFileLog.fileLog;
      const fileLabels = fileLog.fileLabels || [];

      let matchedRequirement = false;
      for (const requirement of documentRequirements) {
        const matchesLabel = fileLabels.some(
          (fl: any) => fl.id === requirement.fileLabel?.id
        );
        if (matchesLabel && requirement.id) {
          const existing = grouped.get(requirement.id) || [];
          grouped.set(requirement.id, [...existing, assetFileLog]);
          matchedRequirement = true;
          break;
        }
      }

      if (!matchedRequirement) {
        otherFiles.push(assetFileLog);
      }
    });

    return { grouped, otherFiles };
  }, [documentFileLogs, documentRequirements]);

  const maintenanceFiles = useMemo(() => {
    return documentFileLogs.filter((assetFileLog: any) => assetFileLog.maintenanceEvent);
  }, [documentFileLogs]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <FileText size={20} weight="duotone" className="text-blue-600" />
        <h3 className="text-md font-semibold">Documenten</h3>
      </div>

      {isLoadingFiles ? (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-5 w-5" />
              <span className="ml-2 text-sm text-muted-foreground">Documenten laden...</span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Required file sections */}
          {documentRequirements.map((req) => {
            const badgeConfig = getRequirementBadgeVariant(req.requirementType);
            const BadgeIcon = badgeConfig.icon;
            const filesForThisReq = req.id ? filesByRequirement.grouped.get(req.id) || [] : [];

            return (
              <Card key={req.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={badgeConfig.variant} className="text-xs">
                          <BadgeIcon size={12} weight="fill" className="mr-1" />
                          {badgeConfig.label}
                        </Badge>
                        <p className="font-semibold text-sm">
                          {req.fileLabel?.label || 'Document'}
                        </p>
                      </div>
                      {req.description && (
                        <p className="text-xs text-muted-foreground mt-1">{req.description}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        navigate('/upload-files', {
                          state: {
                            buildingId: asset.buildingId,
                            unitId: asset.unitId,
                            customData: { assetId: asset.id },
                            defaultFileLabel: req.fileLabel?.label || null,
                          },
                        });
                      }}
                    >
                      <Upload size={14} className="mr-1" />
                      Upload
                    </Button>
                  </div>

                  {/* Suggested files from same building/unit */}
                  {(() => {
                    const suggestions = suggestedFiles.filter(
                      sf => sf.fileLabelName === req.fileLabel?.label
                    );
                    if (suggestions.length === 0) return null;
                    return (
                      <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                        <p className="text-xs font-medium text-amber-700 mb-2">
                          Beschikbare {req.fileLabel?.label || 'bestanden'} in {asset.buildingId || 'dit gebouw'} die nog niet gekoppeld zijn aan een voorziening ({suggestions.length})
                        </p>
                        <div className="space-y-1.5">
                          {suggestions.map(sf => (
                            <div key={sf.id} className="flex items-center justify-between gap-2 bg-white rounded-md px-2.5 py-1.5 border border-amber-100">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <FileText size={14} className="text-amber-600 flex-shrink-0" />
                                <div className="min-w-0">
                                  <p className="text-xs font-medium truncate">{sf.fileLabelName}</p>
                                  {sf.documentDate && (
                                    <p className="text-[10px] text-muted-foreground">
                                      {new Date(sf.documentDate).toLocaleDateString('nl-BE')}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                {sf.sharePointFileUrl && (
                                  <a
                                    href={sf.sharePointFileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:underline px-1.5 py-0.5"
                                    title="Bekijk bestand"
                                  >
                                    Bekijk
                                  </a>
                                )}
                                <button
                                  onClick={() => handleLinkSuggestedFile(sf.id)}
                                  className="text-xs font-medium bg-amber-600 text-white px-2 py-0.5 rounded hover:bg-amber-700 transition-colors"
                                >
                                  Koppelen
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {filesForThisReq.length === 0 ? (
                    <div className="text-center py-6 border-2 border-dashed rounded-lg">
                      <FileText size={32} className="mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Nog niet geüpload</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filesForThisReq.map((assetFileLog) => {
                        const fileLog = assetFileLog.fileLog;
                        const primaryLabel = fileLog.fileLabels?.[0]?.label || fileLog.label?.[0] || req.fileLabel?.label;

                        const getStatusInfo = (status: string) => {
                          const statusMap: Record<string, { label: string; color: string }> = {
                            'OK': { label: 'OK', color: 'bg-green-500' },
                            'NOT_OK': { label: 'Niet OK', color: 'bg-red-500' },
                            'PENDING': { label: 'In behandeling', color: 'bg-yellow-500' },
                            'UNKNOWN': { label: 'Onbekend', color: 'bg-gray-400' },
                          };
                          return statusMap[status] || statusMap['UNKNOWN'];
                        };

                        const statusInfo = getStatusInfo(fileLog.status || 'UNKNOWN');

                        return (
                          <div
                            key={assetFileLog.id}
                            title={fileLog.statusDescription || fileLog.summary || ''}
                            className="flex items-start justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-default"
                          >
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <FileText size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-0.5">
                                  <p className="text-xs font-medium truncate">
                                    {primaryLabel}
                                  </p>
                                  <div
                                    className={`w-2 h-2 rounded-full flex-shrink-0 ${statusInfo.color}`}
                                    title={statusInfo.label}
                                  />
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {fileLog.documentDate && (
                                    <span>
                                      {new Date(fileLog.documentDate).toLocaleDateString('nl-NL')}
                                    </span>
                                  )}
                                  {fileLog.expirationDate && (
                                    <span className="text-orange-600">
                                      Verloopt: {new Date(fileLog.expirationDate).toLocaleDateString('nl-NL')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <a
                              href={fileLog.sharePointFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:text-primary/80 text-xs font-medium whitespace-nowrap ml-2"
                            >
                              Bekijken
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Other documents section (general upload) - ALWAYS show this */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-semibold text-sm">
                  {documentRequirements.length > 0 ? 'Overige Documenten' : 'Algemene Documenten'}
                </p>
                <Button
                  size="sm"
                  onClick={() => {
                    navigate('/upload-files', {
                      state: {
                        buildingId: asset.buildingId,
                        unitId: asset.unitId,
                        customData: { assetId: asset.id },
                      },
                    });
                  }}
                >
                  <Upload size={14} className="mr-1" />
                  Upload
                </Button>
              </div>

              {filesByRequirement.otherFiles.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed rounded-lg">
                  <FileText size={32} className="mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {documentRequirements.length > 0 ? 'Geen extra documenten' : 'Nog geen documenten geüpload'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filesByRequirement.otherFiles.map((assetFileLog) => {
                    const fileLog = assetFileLog.fileLog;
                    const primaryLabel = fileLog.fileLabels?.[0]?.label || fileLog.label?.[0] || 'Document';

                    return (
                      <div
                        key={assetFileLog.id}
                        className="flex items-start justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          <FileText size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {primaryLabel}
                            </p>
                            {fileLog.documentDate && (
                              <p className="text-xs text-muted-foreground">
                                {new Date(fileLog.documentDate).toLocaleDateString('nl-NL')}
                              </p>
                            )}
                          </div>
                        </div>
                        <a
                          href={fileLog.sharePointFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/80 text-xs font-medium whitespace-nowrap ml-2"
                        >
                          Bekijken
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Maintenance reports section */}
          {maintenanceFiles.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="outline" className="text-xs">
                    <Package size={12} className="mr-1" />
                    Onderhoud
                  </Badge>
                  <p className="font-semibold text-sm">Onderhoudsrapporten</p>
                </div>
                <div className="space-y-2">
                  {maintenanceFiles.map((assetFileLog: any) => {
                    const fileLog = assetFileLog.fileLog;
                    const me = assetFileLog.maintenanceEvent;
                    const primaryLabel = fileLog.fileLabels?.[0]?.label || fileLog.label?.[0] || 'Onderhoudsrapport';

                    return (
                      <div
                        key={assetFileLog.id}
                        className="flex items-start justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          <FileText size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{primaryLabel}</p>
                            {me && (
                              <Link
                                to={`/maintenance-event/${me.id}`}
                                className="text-xs text-sky-600 hover:text-sky-800 hover:underline"
                              >
                                {me.scheduleName ? `${me.scheduleName}` : 'Onderhoudsgebeurtenis'}{me.dueDate ? ` – ${new Date(me.dueDate).toLocaleDateString('nl-NL')}` : ''}
                              </Link>
                            )}
                          </div>
                        </div>
                        {fileLog.sharePointFileUrl && (
                          <a
                            href={fileLog.sharePointFileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:text-primary/80 text-xs font-medium whitespace-nowrap ml-2"
                          >
                            Bekijken
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Pictures section component for asset detail page
 */
const AssetPicturesSection: React.FC<{ entity: Asset; onUpdate: (updated: Asset) => void }> = ({
  entity: asset,
}) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [assetFileLogs, setAssetFileLogs] = useState<any[]>([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [fileRequirements, setFileRequirements] = useState<FileRequirement[]>([]);

  // Fetch file logs and requirements
  useEffect(() => {
    if (!asset || !session) return;

    const fetchFileData = async () => {
      try {
        setIsLoadingFiles(true);

        // Fetch asset file logs using proper relationship
        const fileLogsResponse = await fetchWithAuth(
          `${API_URL}/assets/${asset.id}/files`,
          { method: 'GET' },
          session
        );

        if (fileLogsResponse.ok) {
          const data = await fileLogsResponse.json();
          setAssetFileLogs(data);
        }

        // Fetch file requirements from asset type (not category)
        if (asset.assetTypeId) {
          const requirementsResponse = await fetchWithAuth(
            `${API_URL}/file-requirements?assetTypeId=${asset.assetTypeId}`,
            { method: 'GET' },
            session
          );

          if (requirementsResponse.ok) {
            const requirements = await requirementsResponse.json();
            const assetScope = asset.roomId ? 'room' : asset.unitId ? 'unit' : 'building';
            const activeRequirements = requirements.filter((req: FileRequirement) => {
              if (!req.active) return false;
              const scopes = (req as any).scopes;
              if (!scopes || scopes.length === 0) return true;
              return scopes.includes(assetScope);
            });
            setFileRequirements(activeRequirements);
          }
        }
      } catch (error) {
        console.error('Error fetching file data:', error);
      } finally {
        setIsLoadingFiles(false);
      }
    };

    fetchFileData();
  }, [asset, session]);

  // Filter pictures (only include those with picture documentType)
  const pictureFileLogs = useMemo(() => {
    return assetFileLogs.filter((assetFileLog) => {
      const fileLog = assetFileLog.fileLog;
      // Check if any of the fileLabels has documentType 'picture'
      const isPicture = fileLog.fileLabels?.some((label: any) => label.documentType === 'picture');
      return isPicture;
    });
  }, [assetFileLogs]);

  const pictureRequirements = useMemo(() => {
    return fileRequirements.filter((req) =>
      (req.fileLabel as any)?.documentType === 'picture'
    );
  }, [fileRequirements]);

  const getRequirementBadgeVariant = (requirementType: RequirementType | string) => {
    const type = typeof requirementType === 'string' ? requirementType.toUpperCase() : requirementType;

    switch (type) {
      case RequirementType.REQUIRED:
      case 'REQUIRED':
        return { variant: 'destructive' as const, icon: WarningCircle, label: 'Verplicht' };
      case RequirementType.RECOMMENDED:
      case 'RECOMMENDED':
        return { variant: 'outline' as const, icon: Info, label: 'Aanbevolen' };
      case RequirementType.OPTIONAL:
      case 'OPTIONAL':
        return { variant: 'secondary' as const, icon: CheckCircle, label: 'Optioneel' };
      default:
        return { variant: 'secondary' as const, icon: Info, label: 'Optioneel' };
    }
  };

  // Group pictures by their required label
  const picturesByRequirement = useMemo(() => {
    const grouped = new Map<string, any[]>();
    const otherPictures: any[] = [];

    pictureFileLogs.forEach((assetFileLog) => {
      const fileLog = assetFileLog.fileLog;
      const fileLabels = fileLog.fileLabels || [];

      let matchedRequirement = false;
      for (const requirement of pictureRequirements) {
        const matchesLabel = fileLabels.some(
          (fl: any) => fl.id === requirement.fileLabel?.id
        );
        if (matchesLabel && requirement.id) {
          const existing = grouped.get(requirement.id) || [];
          grouped.set(requirement.id, [...existing, assetFileLog]);
          matchedRequirement = true;
          break;
        }
      }

      if (!matchedRequirement) {
        otherPictures.push(assetFileLog);
      }
    });

    return { grouped, otherPictures };
  }, [pictureFileLogs, pictureRequirements]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <ImageIcon size={20} weight="duotone" className="text-purple-600" />
        <h3 className="text-md font-semibold">Foto's</h3>
      </div>

      {isLoadingFiles ? (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-5 w-5" />
              <span className="ml-2 text-sm text-muted-foreground">Foto's laden...</span>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Required picture sections */}
          {pictureRequirements.map((req) => {
            const badgeConfig = getRequirementBadgeVariant(req.requirementType);
            const BadgeIcon = badgeConfig.icon;
            const picturesForThisReq = req.id ? picturesByRequirement.grouped.get(req.id) || [] : [];

            return (
              <Card key={req.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={badgeConfig.variant} className="text-xs">
                          <BadgeIcon size={12} weight="fill" className="mr-1" />
                          {badgeConfig.label}
                        </Badge>
                        <p className="font-semibold text-sm">
                          {req.fileLabel?.label || 'Foto'}
                        </p>
                      </div>
                      {req.description && (
                        <p className="text-xs text-muted-foreground mt-1">{req.description}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigate('/upload-files', {
                          state: {
                            buildingId: asset.buildingId,
                            unitId: asset.unitId,
                            customData: { assetId: asset.id },
                            defaultUploadType: 'picture',
                            defaultFileLabel: req.fileLabel?.label || null,
                          },
                        });
                      }}
                    >
                      <Upload size={14} className="mr-1" />
                      Upload
                    </Button>
                  </div>

                  {picturesForThisReq.length === 0 ? (
                    <div className="text-center py-6 border-2 border-dashed rounded-lg">
                      <ImageIcon size={32} className="mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Nog niet geüpload</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {picturesForThisReq.map((assetFileLog) => {
                        const fileLog = assetFileLog.fileLog;
                        const primaryLabel = fileLog.fileLabels?.[0]?.label || fileLog.label?.[0] || 'Foto';

                        return (
                          <a
                            key={assetFileLog.id}
                            href={fileLog.sharePointFileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative aspect-square rounded-lg overflow-hidden border hover:border-primary/50 transition-all shadow-sm hover:shadow-md"
                          >
                            <img
                              src={fileLog.sharePointFileUrl}
                              alt={primaryLabel}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="absolute bottom-0 left-0 right-0 p-2">
                                <p className="text-white text-xs font-medium truncate">
                                  {fileLog.documentDate &&
                                    new Date(fileLog.documentDate).toLocaleDateString('nl-NL')}
                                </p>
                              </div>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Other pictures section (general upload) */}
          {(picturesByRequirement.otherPictures.length > 0 || pictureRequirements.length === 0) && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-semibold text-sm">
                    {pictureRequirements.length > 0 ? "Overige Foto's" : "Foto's"}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigate('/upload-files', {
                        state: {
                          buildingId: asset.buildingId,
                          unitId: asset.unitId,
                          customData: { assetId: asset.id },
                          defaultUploadType: 'picture',
                        },
                      });
                    }}
                  >
                    <Upload size={14} className="mr-1" />
                    Upload
                  </Button>
                </div>

                {picturesByRequirement.otherPictures.length === 0 ? (
                  <div className="text-center py-6 border-2 border-dashed rounded-lg">
                    <ImageIcon size={32} className="mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Geen extra foto's</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {picturesByRequirement.otherPictures.map((assetFileLog) => {
                      const fileLog = assetFileLog.fileLog;
                      const primaryLabel = fileLog.fileLabels?.[0]?.label || fileLog.label?.[0] || 'Foto';

                      return (
                        <a
                          key={assetFileLog.id}
                          href={fileLog.sharePointFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group relative aspect-square rounded-lg overflow-hidden border hover:border-primary/50 transition-all shadow-sm hover:shadow-md"
                        >
                          <img
                            src={fileLog.sharePointFileUrl}
                            alt={primaryLabel}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="absolute bottom-0 left-0 right-0 p-2">
                              <p className="text-white text-xs font-medium truncate">
                                {primaryLabel}
                              </p>
                              {fileLog.documentDate && (
                                <p className="text-white/80 text-xs">
                                  {new Date(fileLog.documentDate).toLocaleDateString('nl-NL')}
                                </p>
                              )}
                            </div>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Maintenance calendar section for after-content area
 */
const AssetMaintenanceCalendarSection: React.FC<{ entity: Asset; onUpdate: (updated: Asset) => void }> = ({
  entity: asset,
}) => {
  const { schedules, refetch: refetchSchedules } = useAssetMaintenanceSchedules(asset.id);
  const { events, refetch: refetchEvents } = useMaintenanceEvents();
  const [viewMode, setViewMode] = useState<'calendar' | 'timeline'>('calendar');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Filter events to only show those for this asset's schedules
  const assetEvents = useMemo(() => events.filter((e) => e.assetId === asset.id), [events, asset.id]);

  const refetch = useCallback(() => {
    refetchSchedules();
    refetchEvents();
  }, [refetchSchedules, refetchEvents]);

  const renderCreateButton = useCallback(() => (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setCreateDialogOpen(true)}
      className="gap-1.5"
    >
      <Plus className="h-3.5 w-3.5" />
      <span className="text-xs">Toevoegen</span>
    </Button>
  ), []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CalendarBlank size={20} weight="duotone" className="text-orange-600" />
          <h3 className="text-md font-semibold">
            {viewMode === 'calendar' ? 'Onderhoudskalender' : 'Onderhoudstijdlijn'}
          </h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setViewMode(viewMode === 'calendar' ? 'timeline' : 'calendar')}
          title={viewMode === 'calendar' ? 'Toon tijdlijn' : 'Toon kalender'}
          className="gap-1.5"
        >
          {viewMode === 'calendar' ? (
            <>
              <ChartLine className="h-4 w-4" />
              <span className="text-xs">Tijdlijn</span>
            </>
          ) : (
            <>
              <ListBullets className="h-4 w-4" />
              <span className="text-xs">Kalender</span>
            </>
          )}
        </Button>
      </div>
      <Card>
        <CardContent className="pt-6">
          {viewMode === 'calendar' ? (
            <MaintenanceCalendar schedules={schedules} events={assetEvents} onRefetch={refetch} assetId={asset.id} />
          ) : (
            <MaintenanceTimeline
              schedules={schedules}
              onRefetchSchedules={refetch}
              renderCreateButton={renderCreateButton}
            />
          )}
        </CardContent>
      </Card>

      <MaintenanceCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreated={refetch}
        assetId={asset.id}
      />
    </div>
  );
};

/**
 * Sidebar section: Building Cover + Asset Picture + QR Code
 */
const AssetSidebarSection: React.FC<{ entity: Asset; onUpdate: (updated: Asset) => void }> = ({
  entity: asset,
}) => {
  const { session } = useSession();
  const [buildingCoverUrl, setBuildingCoverUrl] = useState<string | null>(null);
  const [assetPictureUrl, setAssetPictureUrl] = useState<string | null>(null);

  // Fetch building cover image
  useEffect(() => {
    if (!asset.buildingId || !session) return;

    const controller = new AbortController();
    let isMounted = true;

    const fetchCoverImage = async () => {
      try {
        const queryParams = new URLSearchParams({
          buildingId: asset.buildingId!,
          unitId: '',
          acquisitionId: '',
          exclusionType: '',
          fileLabel: '',
          secondaryLabel: '',
          page: '1',
          pageSize: '1',
        }).toString();

        const response = await fetchWithAuth(
          `${API_URL}/file/get-images?${queryParams}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ labels: ['Cover foto'] }),
            signal: controller.signal,
          },
          session
        );

        if (!response.ok) throw new Error('Failed to fetch building cover image');

        const images = await response.json();
        if (isMounted && images?.length > 0) {
          const validImages = images.filter((img: any) => img.imageFound);
          if (validImages.length > 0) {
            setBuildingCoverUrl(validImages[0].imageUrl);
          }
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Error fetching building cover:', error);
        }
      }
    };

    fetchCoverImage();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [asset.buildingId, session]);

  // Fetch first asset picture
  useEffect(() => {
    if (!asset.id || !session) return;

    const controller = new AbortController();
    let isMounted = true;

    const fetchAssetPicture = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/assets/${asset.id}/files`,
          { method: 'GET', signal: controller.signal },
          session
        );

        if (!response.ok) throw new Error('Failed to fetch asset files');

        const data = await response.json();
        if (isMounted && data?.length > 0) {
          const pictureFile = data.find((assetFileLog: any) =>
            assetFileLog.fileLog?.fileLabels?.some((label: any) => label.documentType === 'picture')
          );
          if (pictureFile?.fileLog?.sharePointFileUrl) {
            setAssetPictureUrl(pictureFile.fileLog.sharePointFileUrl);
          }
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('Error fetching asset picture:', error);
        }
      }
    };

    fetchAssetPicture();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [asset.id, session]);

  return (
    <div className="space-y-6">
      {/* Building Cover Image */}
      {buildingCoverUrl && (
        <div className="relative rounded-lg overflow-hidden aspect-video">
          <img
            src={buildingCoverUrl}
            alt="Gebouw"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <p className="text-white text-xs font-medium truncate">
              {asset.buildingId}
            </p>
            {asset.building?.company?.name && (
              <p className="text-white/70 text-xs truncate">
                {asset.building.company.name}
              </p>
            )}
          </div>
        </div>
      )}

      {/* First Asset Picture */}
      {assetPictureUrl && (
        <div className="relative rounded-lg overflow-hidden aspect-square">
          <img
            src={assetPictureUrl}
            alt={asset.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <p className="text-white text-xs font-medium truncate">
              {asset.assetCategory?.name}
              {asset.assetType?.name ? ` — ${asset.assetType.name}` : ''}
            </p>
          </div>
        </div>
      )}

      {/* QR Code */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <QrCode size={18} weight="duotone" className="text-primary" />
          <h3 className="text-sm font-semibold">QR Code</h3>
        </div>
        <AssetQRCodeSection assetId={asset.id} assetName={asset.name} />
      </div>
    </div>
  );
};

export const AssetDetailPage: React.FC = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const { session } = useSession();
  const sessionRef = React.useRef(session);
  sessionRef.current = session;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [asset, setAsset] = useState<Asset | null>(null);
  const [allAssets, setAllAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingField, setIsAddingField] = useState(false);
  const [childAssets, setChildAssets] = useState<any[]>([]);

  // Hook for creating type field definitions
  const { createTypeFieldDefinition, isCreating: isCreatingField } = useCreateTypeFieldDefinition();

  // Fetch single asset
  useEffect(() => {
    if (!assetId || !sessionRef.current) return;

    const fetchAsset = async () => {
      try {
        setIsLoading(true);
        const response = await fetchWithAuth(
          `${API_URL}/assets/${assetId}`,
          { method: 'GET' },
          sessionRef.current!
        );

        if (!response.ok) {
          throw new Error('Failed to fetch asset');
        }

        const data = await response.json();

        // If asset has a category but no field definitions, fetch them separately
        if (data.assetCategory && !data.assetCategory.fieldDefinitions) {
          const categoryResponse = await fetchWithAuth(
            `${API_URL}/asset-categories/${data.assetCategoryId}`,
            { method: 'GET' },
            sessionRef.current!
          );

          if (categoryResponse.ok) {
            const categoryData = await categoryResponse.json();
            // Merge the full category data with field definitions
            data.assetCategory = categoryData;
          }
        }

        // If asset has a type but no field definitions, fetch them separately
        if (data.assetTypeId && (!data.assetType || !data.assetType.fieldDefinitions)) {
          const typeResponse = await fetchWithAuth(
            `${API_URL}/asset-categories/${data.assetCategoryId}/types/${data.assetTypeId}/details`,
            { method: 'GET' },
            sessionRef.current!
          );

          if (typeResponse.ok) {
            const typeData = await typeResponse.json();
            // Merge the full type data with field definitions
            data.assetType = typeData;
          }
        }

        setAsset(data);
      } catch (error) {
        console.error('Error fetching asset:', error);
        toast.error('Fout bij laden van voorziening');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAsset();
  }, [assetId]);

  // Refetch asset data (used after creating a new field)
  const refetchAsset = useCallback(async () => {
    if (!assetId || !sessionRef.current) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/assets/${assetId}`,
        { method: 'GET' },
        sessionRef.current
      );

      if (!response.ok) {
        throw new Error('Failed to fetch asset');
      }

      const data = await response.json();

      // If asset has a category but no field definitions, fetch them separately
      if (data.assetCategory && !data.assetCategory.fieldDefinitions) {
        const categoryResponse = await fetchWithAuth(
          `${API_URL}/asset-categories/${data.assetCategoryId}`,
          { method: 'GET' },
          sessionRef.current!
        );

        if (categoryResponse.ok) {
          const categoryData = await categoryResponse.json();
          data.assetCategory = categoryData;
        }
      }

      // If asset has a type but no field definitions, fetch them separately
      if (data.assetTypeId && (!data.assetType || !data.assetType.fieldDefinitions)) {
        const typeResponse = await fetchWithAuth(
          `${API_URL}/asset-categories/${data.assetCategoryId}/types/${data.assetTypeId}/details`,
          { method: 'GET' },
          sessionRef.current!
        );

        if (typeResponse.ok) {
          const typeData = await typeResponse.json();
          data.assetType = typeData;
        }
      }

      setAsset(data);
    } catch (error) {
      console.error('Error refetching asset:', error);
    }
  }, [assetId]);

  // Handle creating a new field definition for the asset's type
  const handleCreateField = useCallback(async (fieldData: NewFieldData) => {
    if (!asset?.assetCategoryId || !asset?.assetTypeId) {
      toast.error('Kan geen veld toevoegen: asset heeft geen type');
      return;
    }

    try {
      await createTypeFieldDefinition(
        asset.assetCategoryId.toString(),
        asset.assetTypeId.toString(),
        {
          fieldName: fieldData.fieldName,
          displayName: fieldData.displayName,
          fieldType: fieldData.fieldType,
          isRequired: fieldData.isRequired,
          enumValues: fieldData.enumValues,
          displayOrder: 999, // Will be sorted on backend
        }
      );

      toast.success(`Veld "${fieldData.displayName}" succesvol toegevoegd`);
      setIsAddingField(false);

      // Refetch to get updated field definitions
      await refetchAsset();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fout bij toevoegen veld';
      toast.error(errorMessage);
      console.error('Error creating field:', error);
      throw error; // Re-throw so InlineFieldCreator knows it failed
    }
  }, [asset, createTypeFieldDefinition, refetchAsset]);

  // Fetch all assets for navigation (filtered by URL params if any)
  useEffect(() => {
    if (!sessionRef.current) return;

    const fetchAllAssets = async () => {
      try {
        // Build query string from search params for filtering
        const queryParams = new URLSearchParams();

        const buildingFilter = searchParams.get('building');
        const unitFilter = searchParams.get('unit');
        const roomFilter = searchParams.get('room');
        const categoryFilter = searchParams.get('category');
        const statusFilter = searchParams.get('status');

        if (buildingFilter) queryParams.append('buildingId', buildingFilter);
        if (unitFilter) queryParams.append('unitId', unitFilter);
        if (roomFilter) queryParams.append('roomId', roomFilter);
        if (categoryFilter) queryParams.append('categoryId', categoryFilter);
        if (statusFilter) queryParams.append('status', statusFilter);

        const queryString = queryParams.toString();
        const url = queryString ? `${API_URL}/assets?${queryString}` : `${API_URL}/assets`;

        const response = await fetchWithAuth(url, { method: 'GET' }, sessionRef.current!);

        if (!response.ok) {
          throw new Error('Failed to fetch assets');
        }

        const data = await response.json();
        setAllAssets(data);
      } catch (error) {
        console.error('Error fetching all assets:', error);
      }
    };

    fetchAllAssets();
  }, [searchParams]);

  // Enhanced entity config with custom sections AND dynamic custom fields
  const enhancedConfig = useMemo((): UniversalEntityConfig => {
    if (!asset || !asset.assetCategory) {
      return {
        ...ASSET_ENTITY_CONFIG,
        plugins: {
          ...ASSET_ENTITY_CONFIG.plugins,
          customSections: [],
        },
      };
    }

    // Build custom field configs from both category and type field definitions
    const customFieldConfigs: UniversalFieldConfig[] = [];

    // Category-level fields (shared across all types in category) — filter enum options by asset type
    if (asset.assetCategory.fieldDefinitions) {
      asset.assetCategory.fieldDefinitions
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .forEach((fieldDef) => {
          customFieldConfigs.push(convertFieldDefinitionToFieldConfig(fieldDef, asset.assetTypeId));
        });
    }

    // Type-level fields (specific to this asset type)
    if (asset.assetType?.fieldDefinitions) {
      asset.assetType.fieldDefinitions
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .forEach((fieldDef) => {
          customFieldConfigs.push(convertFieldDefinitionToFieldConfig(fieldDef, asset.assetTypeId));
        });
    }

    return {
      ...ASSET_ENTITY_CONFIG,
      // Merge base fields with custom fields
      fields: [...ASSET_ENTITY_CONFIG.fields, ...customFieldConfigs],
      plugins: {
        ...ASSET_ENTITY_CONFIG.plugins,
        customSections: [
          // Documents section
          {
            id: 'documents',
            position: 'after-content' as const,
            component: AssetDocumentsSection,
          },
          // Pictures section
          {
            id: 'pictures',
            position: 'after-content' as const,
            component: AssetPicturesSection,
          },
          // Child records (meterstanden etc.)
          {
            id: 'child-records',
            position: 'after-content' as const,
            component: AssetChildRecordsSection,
          },
          // Maintenance calendar
          {
            id: 'maintenance-calendar',
            position: 'after-content' as const,
            component: AssetMaintenanceCalendarSection,
          },
          // Sidebar: QR Code + Maintenance
          {
            id: 'sidebar',
            position: 'sidebar' as const,
            component: AssetSidebarSection,
          },
        ],
      },
    };
  }, [asset]);

  // Get existing field names for validation
  const existingFieldNames = useMemo(() => {
    const names: string[] = [];

    // Category-level fields
    if (asset?.assetCategory?.fieldDefinitions) {
      names.push(...asset.assetCategory.fieldDefinitions.map((f) => f.fieldName));
    }

    // Type-level fields
    if (asset?.assetType?.fieldDefinitions) {
      names.push(...asset.assetType.fieldDefinitions.map((f) => f.fieldName));
    }

    return names;
  }, [asset]);

  // Render the inline field creator
  // Fetch child assets (sub-meters, etc.)
  useEffect(() => {
    if (!asset?.id || !session) return;
    fetchWithAuth(`${API_URL}/assets?buildingId=${asset.buildingId || ''}&categoryId=${asset.assetCategoryId}`, { method: 'GET' }, session)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const children = (Array.isArray(data) ? data : []).filter((a: any) => a.parentAssetId === asset.id);
        setChildAssets(children);
      })
      .catch(() => {});
  }, [asset?.id, session]);

  const renderAfterFields = useCallback(() => {
    if (!asset?.assetTypeId) return null;

    if (isAddingField) {
      return (
        <InlineFieldCreator
          onCreateField={handleCreateField}
          isCreating={isCreatingField}
          existingFieldNames={existingFieldNames}
          onCancel={() => setIsAddingField(false)}
        />
      );
    }

    return (
      <>
        {/* Add field button */}
        <button
          onClick={() => setIsAddingField(true)}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-muted-foreground/30 rounded-lg text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span className="text-sm">Veld toevoegen</span>
        </button>

        {/* Parent asset link */}
        {asset?.parentAsset?.id && (
          <div className="mt-4 rounded-lg border bg-blue-50/50 p-3">
            <p className="text-xs font-medium text-muted-foreground mb-2">Bovenliggende voorziening</p>
            <a
              href={`/asset/${asset.parentAsset.id}`}
              className="flex items-center gap-2 text-sm text-primary hover:underline font-medium"
            >
              <span className="text-lg">↑</span>
              {asset.parentAsset.name || asset.parentAsset.id}
            </a>
          </div>
        )}

        {/* Child assets tree */}
        {childAssets.length > 0 && (
          <div className="mt-4 rounded-lg border p-3">
            <p className="text-xs font-medium text-muted-foreground mb-3">
              Gekoppelde voorzieningen ({childAssets.length})
            </p>
            <div className="space-y-1">
              {childAssets.map((child: any) => (
                <a
                  key={child.id}
                  href={`/asset/${child.id}`}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 transition-colors group"
                >
                  <span className="text-muted-foreground text-xs">└─</span>
                  <span className="text-sm font-medium text-foreground group-hover:text-primary">{child.name}</span>
                  {child.unitId && (
                    <span className="text-xs text-muted-foreground ml-auto">{child.unitId}</span>
                  )}
                  {child.fieldValues?.meternummer && (
                    <span className="text-xs text-muted-foreground">#{child.fieldValues.meternummer}</span>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}
      </>
    );
  }, [asset, isAddingField, handleCreateField, isCreatingField, existingFieldNames, childAssets]);

  // Handle loading state
  if (isLoading || !asset) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner className="h-8 w-8 mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground">Voorziening laden...</p>
        </div>
      </div>
    );
  }

  // Handle not found
  if (!asset) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Package size={64} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-xl font-semibold mb-2">Voorziening niet gevonden</p>
          <Button onClick={() => navigate('/assets')}>
            Terug naar overzicht
          </Button>
        </div>
      </div>
    );
  }

  return (
    <UniversalDetailPage
      entityConfig={enhancedConfig}
      entityId={assetId}
      initialEntity={asset}
      allEntities={allAssets}
      renderAfterFields={renderAfterFields}
    />
  );
};
